import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowDownToLine, CreditCard, Banknote } from "lucide-react";

const METHOD_LABEL = { bank: "Transferencia bancaria", paypal: "PayPal", wise: "Wise", usdc: "USDC (crypto)" };
const STATUS_LABEL = { requested: "Solicitado", processing: "En proceso", paid: "Pagado", rejected: "Rechazado" };

export default function Wallet() {
  const [balance, setBalance] = useState(null);
  const [methods, setMethods] = useState(null);
  const [history, setHistory] = useState([]);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdForm, setWdForm] = useState({ amount: "", method: "paypal", note: "" });
  const [savingMethods, setSavingMethods] = useState(false);

  const load = async () => {
    try {
      const [b, m, h] = await Promise.all([
        api.get("/wallet/balance"),
        api.get("/users/me/payout-methods"),
        api.get("/withdrawals/mine"),
      ]);
      setBalance(b.data); setMethods(m.data); setHistory(h.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const saveMethods = async (e) => {
    e.preventDefault();
    setSavingMethods(true);
    try {
      await api.put("/users/me/payout-methods", methods);
      toast.success("Métodos de retiro guardados");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingMethods(false); }
  };

  const submitWithdrawal = async (e) => {
    e.preventDefault();
    try {
      await api.post("/withdrawals", {
        amount: parseFloat(wdForm.amount),
        method: wdForm.method,
        note: wdForm.note,
      });
      toast.success("Retiro solicitado");
      setWdOpen(false);
      setWdForm({ amount: "", method: "paypal", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!balance) return null;

  const updateBank = (k, v) => setMethods({ ...methods, bank: { ...(methods.bank || {}), [k]: v } });

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <WalletIcon className="w-5 h-5 text-primary" /> Wallet
          </h2>
          <p className="text-xs text-muted-foreground mt-1">Comisión de plataforma: {balance.commission_pct}%</p>
        </div>
        <Dialog open={wdOpen} onOpenChange={setWdOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" disabled={balance.available <= 0} data-testid="request-withdrawal-btn">
              <ArrowDownToLine className="w-4 h-4 mr-2" /> Solicitar retiro
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo retiro</DialogTitle>
              <DialogDescription>Disponible: ${balance.available.toFixed(2)} USD · Mínimo $20.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitWithdrawal} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase font-semibold">Método</Label>
                  <Select value={wdForm.method} onValueChange={(v) => setWdForm({ ...wdForm, method: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="withdrawal-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Transferencia bancaria</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="wise">Wise</SelectItem>
                      <SelectItem value="usdc">USDC (crypto)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase font-semibold">Monto (USD)</Label>
                  <Input type="number" min="20" max={balance.available} required value={wdForm.amount}
                    onChange={(e) => setWdForm({ ...wdForm, amount: e.target.value })}
                    className="mt-1.5 h-11" data-testid="withdrawal-amount-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Nota (opcional)</Label>
                <Textarea rows={2} value={wdForm.note} onChange={(e) => setWdForm({ ...wdForm, note: e.target.value })}
                  className="mt-1.5" data-testid="withdrawal-note" />
              </div>
              <Button type="submit" className="rounded-full w-full h-11" data-testid="submit-withdrawal-btn">
                Solicitar retiro
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-5"><Banknote className="w-4 h-4 text-primary mb-2" /><div className="text-2xl font-heading font-bold" data-testid="wallet-available">${balance.available.toFixed(0)}</div><div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Disponible</div></CardContent></Card>
        <Card><CardContent className="p-5"><WalletIcon className="w-4 h-4 text-primary mb-2" /><div className="text-2xl font-heading font-bold" data-testid="wallet-balance">${balance.balance.toFixed(0)}</div><div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Balance total</div></CardContent></Card>
        <Card><CardContent className="p-5"><ArrowDownToLine className="w-4 h-4 text-amber-500 mb-2" /><div className="text-2xl font-heading font-bold" data-testid="wallet-pending">${balance.pending_withdrawals.toFixed(0)}</div><div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Retiros pendientes</div></CardContent></Card>
        <Card><CardContent className="p-5"><CreditCard className="w-4 h-4 text-emerald-500 mb-2" /><div className="text-2xl font-heading font-bold" data-testid="wallet-total-earned">${balance.total_earned.toFixed(0)}</div><div className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">Total ganado</div></CardContent></Card>
      </div>

      <Tabs defaultValue="methods">
        <TabsList>
          <TabsTrigger value="methods" data-testid="tab-methods">Métodos de retiro</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historial ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="methods" className="mt-4">
          <Card><CardContent className="p-6">
            <form onSubmit={saveMethods} className="space-y-6">
              <div>
                <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground mb-3">Transferencia bancaria internacional</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Input placeholder="Titular de la cuenta" value={methods?.bank?.holder_name || ""} onChange={(e) => updateBank("holder_name", e.target.value)} className="h-11" data-testid="bank-holder" />
                  <Input placeholder="Nombre del banco" value={methods?.bank?.bank_name || ""} onChange={(e) => updateBank("bank_name", e.target.value)} className="h-11" data-testid="bank-name" />
                  <Input placeholder="IBAN / número de cuenta" value={methods?.bank?.account_number || ""} onChange={(e) => updateBank("account_number", e.target.value)} className="h-11" data-testid="bank-account" />
                  <Input placeholder="SWIFT / BIC" value={methods?.bank?.swift_bic || ""} onChange={(e) => updateBank("swift_bic", e.target.value)} className="h-11" data-testid="bank-swift" />
                  <Input placeholder="País" value={methods?.bank?.country || ""} onChange={(e) => updateBank("country", e.target.value)} className="h-11" data-testid="bank-country" />
                  <Input placeholder="Moneda (ej. USD)" value={methods?.bank?.currency || "USD"} onChange={(e) => updateBank("currency", e.target.value)} className="h-11" data-testid="bank-currency" />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase font-semibold">PayPal (email)</Label>
                  <Input type="email" placeholder="tu@paypal.com" value={methods?.paypal_email || ""} onChange={(e) => setMethods({ ...methods, paypal_email: e.target.value })} className="mt-1.5 h-11" data-testid="paypal-email" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase font-semibold">Wise (email)</Label>
                  <Input type="email" placeholder="tu@wise.com" value={methods?.wise_email || ""} onChange={(e) => setMethods({ ...methods, wise_email: e.target.value })} className="mt-1.5 h-11" data-testid="wise-email" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase font-semibold">USDC (wallet)</Label>
                  <Input placeholder="0x…" value={methods?.usdc_wallet || ""} onChange={(e) => setMethods({ ...methods, usdc_wallet: e.target.value })} className="mt-1.5 h-11" data-testid="usdc-wallet" />
                </div>
              </div>
              <Button type="submit" disabled={savingMethods} className="rounded-full" data-testid="save-methods-btn">
                {savingMethods ? "Guardando…" : "Guardar métodos"}
              </Button>
            </form>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
              Aún no has solicitado retiros.
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((w) => (
                <Card key={w.withdrawal_id} data-testid={`withdrawal-${w.withdrawal_id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{METHOD_LABEL[w.method]}</div>
                      <div className="font-medium">${w.amount.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-sm ${
                      w.status === 'paid' ? 'bg-emerald-500/10 text-emerald-600' :
                      w.status === 'rejected' ? 'bg-destructive/10 text-destructive' :
                      'bg-amber-500/10 text-amber-600'
                    }`}>{STATUS_LABEL[w.status] || w.status}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
